import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Users, Receipt, Coins, SplitSquareHorizontal, CheckCircle } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresBillingSection: React.FC = () => {
  const { t } = useTranslation('bares')
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const images = [
    { src: '/images/bares/10-admin-mesas-desktop.png', alt: 'Admin - Mesas', title: 'Admin - Mesas', description: 'Mesas abertas e ações rápidas.' },
    { src: '/images/bares/11-admin-quitar-pessoa-desktop.png', alt: 'Admin - Quitar por pessoa', title: 'Quitar por pessoa', description: 'Selecione pessoas específicas ou grupos para pagamento.' }
  ]

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-green-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4"
          >
            <Receipt className="w-4 h-4" />
            {t('billing.kicker', 'Fechamento Inteligente')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {t('billing.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('billing.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <strong className="text-xl text-gray-900">{t('billing.cards.perGuest.title')}</strong>
            </div>
            <p className="text-gray-700 mb-6">{t('billing.cards.perGuest.desc')}</p>
            <div className="bg-white/70 rounded-xl p-4 backdrop-blur shadow-inner">
              {[['Maria', '36,00'], ['João', '28,00'], ['Ana', '25,00']].map(([n, v]) => (
                <div key={n} className="flex justify-between text-gray-800 py-2 border-b border-gray-200 last:border-0">
                  <span className="font-medium">{n}</span>
                  <span className="font-bold text-amber-600">R$ {v}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl border border-orange-200 p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <SplitSquareHorizontal className="w-6 h-6 text-orange-600" />
              </div>
              <strong className="text-xl text-gray-900">{t('billing.cards.split.title')}</strong>
            </div>
            <p className="text-gray-700 mb-6">{t('billing.cards.split.desc')}</p>
            <div className="bg-white/70 rounded-xl p-4 backdrop-blur shadow-inner">
              <div className="text-xs text-gray-600 mb-3 font-semibold">{t('billing.cards.split.example')}</div>
              <div className="flex justify-between text-gray-800 py-2 border-b border-gray-200">
                <span className="font-medium">Maria + Ana</span>
                <span className="font-bold text-orange-600">R$ 61,00</span>
              </div>
              <div className="flex justify-between text-gray-800 py-2">
                <span className="font-medium">João</span>
                <span className="font-bold text-orange-600">R$ 28,00</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl border border-yellow-200 p-8 shadow-lg"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-yellow-600" />
              </div>
              <strong className="text-xl text-gray-900">{t('billing.cards.payment.title')}</strong>
            </div>
            <p className="text-gray-700 mb-6">{t('billing.cards.payment.desc')}</p>
            <div className="bg-white/70 rounded-xl p-4 backdrop-blur shadow-inner">
              <div className="text-xs text-gray-600 mb-3 font-semibold">PIX · Cartão · Dinheiro</div>
              <div className="flex justify-between text-gray-800 py-2 border-b border-gray-200">
                <span className="font-medium">PIX Maria</span>
                <span className="text-gray-600">R$ 36,00</span>
              </div>
              <div className="flex justify-between text-gray-800 py-2 border-b border-gray-200">
                <span className="font-medium">Admin (Cartão)</span>
                <span className="text-gray-600">R$ 53,00</span>
              </div>
              <div className="flex justify-between text-green-700 py-2 font-bold">
                <span>{t('billing.cards.payment.due')}</span>
                <span className="text-xl">R$ 0,00 ✓</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-8 shadow-lg mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-amber-600" />
            </div>
            <strong className="text-xl text-gray-900">{t('billing.autoClose.title')}</strong>
          </div>
          <p className="text-gray-700 text-lg">{t('billing.autoClose.desc')}</p>
        </motion.div>

        {/* Admin Screenshots */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {images.map((img, i) => (
            <motion.button
              key={img.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              onClick={() => { setIndex(i); setOpen(true) }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full rounded-xl border-2 border-white shadow-md group-hover:scale-[1.02] transition-transform duration-300"
              />
              <div className="mt-4 font-semibold text-gray-700 group-hover:text-amber-600 transition-colors">{img.description}</div>
            </motion.button>
          ))}
        </div>
        <ImageGallery images={images} isOpen={open} onClose={() => setOpen(false)} initialIndex={index} />
      </div>
    </section>
  )
}
